import type { ILayerProps } from '@/version-types';

export type TDrawFrameTarget =
    | { type: 'passthrough' }
    | { type: 'cancel' }
    | { type: 'target'; frameId: number; offsetTop: number };

interface IResolveDrawFrameTargetOptions {
    isLongImageMode: boolean;
    drawingFrameId: number | undefined;
    checkFrameIsRender: (frameId: number) => boolean;
    getFrameOffsetTop: (frameId: number) => number;
}

interface ICancelCurrentDrawOptions {
    generation: number;
    isCurrent: (generation: number) => boolean;
    resetInteraction: () => void;
    clearDrawConfig: () => void;
}

/** 将本次命中的渲染索引转换并锁定为稳定 frame ID。 */
export function resolveDrawingFrameId(
    isLongImageMode: boolean,
    frameIndex: number,
    indexToFrameId: (frameIndex: number) => number,
) {
    return isLongImageMode ? indexToFrameId(frameIndex) : undefined;
}

/** 解析绘制落图目标；非长画布沿用原有图层添加和 frame 吸入流程。 */
export function resolveDrawFrameTarget({
    isLongImageMode,
    drawingFrameId,
    checkFrameIsRender,
    getFrameOffsetTop,
}: IResolveDrawFrameTargetOptions): TDrawFrameTarget {
    if (!isLongImageMode || drawingFrameId === undefined) {
        return { type: 'passthrough' };
    }
    if (!checkFrameIsRender(drawingFrameId)) {
        return { type: 'cancel' };
    }
    return {
        type: 'target',
        frameId: drawingFrameId,
        offsetTop: getFrameOffsetTop(drawingFrameId),
    };
}

/** 将长画布全局场景坐标转换为目标页内坐标，并显式指定目标 frame。 */
export function applyDrawFrameTarget(layer: ILayerProps, target: Extract<TDrawFrameTarget, { type: 'target' }>) {
    layer.top -= target.offsetTop;
    return {
        layer,
        destOriginIndex: target.frameId,
    };
}

/** 只清理仍属于当前代的无效目标绘制，避免旧任务覆盖新一代交互。 */
export function cancelCurrentDraw({
    generation,
    isCurrent,
    resetInteraction,
    clearDrawConfig,
}: ICancelCurrentDrawOptions) {
    if (!isCurrent(generation)) return false;
    resetInteraction();
    clearDrawConfig();
    return true;
}
